import { createColumnHelper, RowModel } from "@tanstack/react-table";

import { IVehicle } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import AwaitingInventoryStatusDropdown from "../awaitingInventory/AwaitingInventoryStatusDropdown";
import PartsPriorityDropdown from "../awaitingInventory/PartsPriorityDropdown";
import YardStatusDropdown from "../awaitingInventory/YardStatusDropdown";
import PagedTable from "../shared/Tables/PagedTable";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import VehicleLocationDropdown from "./VehicleLocationDropdown";
import PartVehicleDetails from "../leads/LeadParts/PartVehicleDetails";
import { HTMLProps, useEffect, useRef } from "react";
import { CameraIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import ClickToCopy from "../shared/ClickToCopy";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";
import PaymentStatusCell from "../payments/PaymentStatusCell";
import { PageMode } from "../../models/PagedTable";
import LeadStatusCell from "../leads/LeadStatusCell";
import VehicleCodCell from "./VehicleCodCell";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import PartVehicleImages from "../leads/LeadParts/LeadImagesCarousel";
import VehicleJobsTableCell from "./VehicleJobsTableCell";
import ExportOffersTableRow from "../exports/ExportOffersTableRow";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IVehicle>();

const cols = [
  {
    id: 'select',
    // header: ({ table }: any) => (
    //   <IndeterminateCheckbox
    //     {...{
    //       checked: table.getIsAllRowsSelected(),
    //       indeterminate: table.getIsSomeRowsSelected(),
    //       onChange: table.getToggleAllRowsSelectedHandler(),
    //     }}
    //   />
    // ),
    cell: ({ row }: any) => (
      <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
        {/* <IndeterminateCheckbox
          {...{
            id: row.original.id,
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        /> */}
        <button
          onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
          style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
          className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
        >
          {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    ),
  },
  colHelper.display({
    id: "image",
    cell: (info) => (
      <span className="flex justify-center relative">
        {info.row.original?.vehicleImageUrl ? (
          <img
            className="w-32 h-20 object-contain"
            src={info.row.original.vehicleImageUrl}
          />
        ) : (
          <CameraIcon className="w-14 text-gray-600" />
        )}
      </span>
    ),
  }),
  colHelper.accessor("vrm", {
    header: "Details",
    id: "vrm",
    sortingFn: "auto",
    cell: (info) => <div className="flex flex-col">
      <ClickToCopy title="VRM" text={info.row.original.vrm} />
      <span className="Make">{info.row.original.combinedMake ?? ""}</span>
      <span title="Model">{info.row.original.combinedModel ?? ""}</span>
      <ClickToCopy title="VIN" text={info.row.original.combinedVIN} textClassName="text-xs" />
      <ClickToCopy title="Engine Model Code" text={info.row.original.engineModelCode} textClassName="text-xs" />
      <span title="Transmission and Fuel Type" className="text-xs">{info.row.original.transmission ?? ""} {info.row.original.fuelType ?? ""}</span>
    </div>,
  }),
  colHelper.accessor("leads", {
    header: "Price",
    enableSorting: false,
    cell: (info) => <span>{PriceFormat(info.row.original.leads[0]?.price)}</span>,
  }),
  colHelper.accessor("vehicleStarting", {
    header: "Vehicle",
    enableSorting: false,
    cell: (info) => <div className="w-24"><VehicleIcons vehicle={info.row.original!} /></div>,
  }),
  colHelper.display({
    header: "Vehicle Status",
    cell: (info) => <div className="flex flex-col space-y-4">
      <span></span>
      {info.row.original.leads?.length > 0 && (
        <>
          <LeadStatusCell status={info.row.original.leads[0].status} />
          <PaymentStatusCell lead={info.row.original.leads[0]} />
          <VehicleCodCell vehicle={info.row.original} />
        </>
      )}
    </div>,
  }),
  colHelper.display({
    header: "Jobs",
    enableSorting: false,
    cell: (info) => <VehicleJobsTableCell lead={info.row.original.leads[0]} vehicle={info.row.original} />,
  }),
  colHelper.display({
    header: "Export Offers",
    enableSorting: false,
    cell: (info) => <ExportOffersTableRow vehicle={info.row.original} />,
  }),
  colHelper.accessor("engineExportStatus", {
    header: "Part Status",
    enableSorting: false,
    cell: (info) => <div id={info.row.original.id}>
      <p>Parts Status</p>
      <AwaitingInventoryStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Parts Priority Status</p>
      <PartsPriorityDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
    </div>
  }),
  colHelper.accessor("yardStatus", {
    header: "Yard Status",
    enableSorting: false,
    cell: (info) => <div id={info.row.original.id}>
      <p className="mt-2">Yard Location</p>
      <VehicleLocationDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Yard Status</p>
      <YardStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
    </div>
  }),
  colHelper.accessor("created", {
    header: "Created",
    enableSorting: false,
    cell: (info) => <span>{formatDateTime(info.getValue())}</span>
  }),
];


const expandedRow = (vehicle: IVehicle) => {


  return (
    <div className="flex flex-col gap-y-2">
      <PartVehicleDetails vehicle={vehicle} />

      <PartVehicleImages lead={vehicle.leads[0]} />
    </div>
  )
}

type VehicleTableProps = {
  additionalBody?: Record<string, unknown>;
};

export default function VehicleTable({ additionalBody }: VehicleTableProps) {

  return (
    <PagedTable
      columns={cols}
      url="/vehicle"
      pageMode={PageMode.Stock}// not using this
      queryLabel="Search vehicles VRM, VIN, Make, Model, Engine Code and Location Name"
      expandedRow={(vehicle) => expandedRow(vehicle)}
      onRowClick={(x: IVehicle) => openInNewTab(`vehicles/${x.id}`)}
      additionalBody={{
        includeParts: true,
        includePartImages: true,
        includeCatalyticResults: true,
        includeExportOffers: true,
        ...(additionalBody ?? {})
      }}
    />
  );
}
